import { enFrequencyType } from 'enums/enFrequencyType';
import { enParameters } from 'enums/enParameters';
import {
  enPaymentMethod,
  enNormalizedPaymentType
} from 'enums/enPaymentMethod';
import { ICartModel } from 'interfaces/ICartModel';
import { ICouponModel } from 'interfaces/ICouponModel';
import { IPaymentResult } from 'interfaces/IPaymentResult';
import { enLang } from 'interfaces/IProductData';
import { IUserAddressModel } from 'interfaces/IUserAddressModel';
import get from 'lodash/get';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { cartService } from 'services/CartService';
import { ADYEN_KEY, MAX_CREDIT_CARDS, GOOFY_ID } from 'settings';
import { round10, round2 } from 'shared/functions';

import { IPaymentModel } from '../interfaces/IPaymentModel';
import { showAlert } from './../shared/services/dialog';
import { brandService } from './BrandService';
import { getInstallments } from './InstallmentsService';
import { langService } from './LangService';
import { getCitiesByState } from './LocationService';
import { parameterService } from './ParameterService';
import { apiService } from 'shared/services';
import { hotJarService } from './HotJar';
import { IThankYouPage } from 'interfaces/IThankYouPage';
import { paymentProgressService } from 'services/PaymentProgressService';
import { enPaymentStatus } from 'enums/enPaymentStatus';

export class PaymentService {
  private state = <ICartModel>{
    payment: [{}]
  };

  private data$: BehaviorSubject<ICartModel>;
  private paymentResult$ = new ReplaySubject<IPaymentResult>(1);
  private paymentSent$ = new BehaviorSubject<boolean>(false);

  private hasPaymentResult = false;
  private paymentInProgress = false;

  constructor(state: ICartModel = null) {
    if (state) {
      this.state = state;
    }

    this.data$ = new BehaviorSubject(this.state);

    Observable.combineLatest(parameterService.get(enParameters.df)).subscribe(
      ([df]) => {
        df = (df || '').replace(/[^0-9]/, '');
        if (df.length === 11) {
          this.setData({ document: df });
        }
      }
    );

    this.data$
      .asObservable()
      .map(state => state.payment.map(p => p.value))
      .filter(v => v[0] != null)
      .map(v => JSON.stringify(v))
      .debounceTime(500)
      .distinctUntilChanged()
      .switchMap(() => this.data$.asObservable().take(1))
      .switchMap(state => {
        return Observable.combineLatest(
          getInstallments(state.payment.map(p => p.value)),
          Observable.of(state),
          langService.getLang()
        );
      })
      .filter(([installments, state, lang]) => lang === enLang.PT_BR)
      .subscribe(([baseInstallments, state, lang]) => {
        let installments = Object.values(baseInstallments);

        state.payment.map((p, key) => {
          const value = p.value;

          const i = installments.filter(installment => {
            installment.installments.sort((a, b) => b.value - a.value);
            return installment.installments[0].value == value;
          });

          if (i.length === 0) {
            return;
          }

          const installment = i[0];

          const selected = get(
            this.state.payment[key],
            'numberInstallments',
            0
          );

          this.setPaymentData(key, {
            installments: installment.installments,
            numberInstallments:
              installment.maxInstallments >= selected
                ? selected
                : installment.maxInstallments
          });
        });
      });

    this.emit();
  }

  hasPaymentInProgress(): boolean {
    return this.paymentInProgress;
  }

  hasPaymentSent(): Observable<boolean> {
    return this.paymentSent$.asObservable().distinctUntilChanged();
  }

  pay(): Observable<IPaymentResult> {
    this.paymentInProgress = true;
    this.paymentSent$.next(true);

    paymentProgressService.setPaymentProgressStatus({
      status: enPaymentStatus.PROGRESS
    });

    return cartService
      .getCart()
      .filter(data => !data.isCached)
      .map(cart => cart.price)
      .combineLatest(
        langService.getLang(),
        cartService.getCart(),
        parameterService.getAll(),
        Observable.fromPromise(import('adyen-cse-web'))
      )
      .take(1)
      .switchMap(([price, lang, cart, parameters, adyen]) => {
        const sumValuePayment = round10(
          this.state.payment.reduce((acc, p) => acc + p.value, 0)
        );

        if (sumValuePayment < round2(price)) {
          return Observable.throw(
            new Error('Por favor, verifique a soma dos valores dos cartões.')
          );
        }

        const cartData: ICartModel = JSON.parse(JSON.stringify(this.state));

        const data = this.normalizePaymentData(adyen, cartData, lang);

        const paymentMethod: any = data.payment[0].paymentMethod;

        let url = '/pay/creditcard';

        if (paymentMethod === enNormalizedPaymentType.BANKSLIP) {
          url = '/pay/bankslip';
        }

        if (paymentMethod === enNormalizedPaymentType.FREE) {
          url = '/pay/free';
        }

        if (paymentMethod === enNormalizedPaymentType.PAYPAL) {
          url = '/pay/paypal';
        }

        if (cart.config.isOneClick) {
          url = '/pay/oneclickbuy';
        }

        if (cart.subscription && cart.subscription.hasTrial) {
          url = '/pay/creditcardtrial';
        }

        if (
          cart.subscription &&
          cart.subscription.hasTrial &&
          paymentMethod === enNormalizedPaymentType.BANKSLIP
        ) {
          url = '/pay/trial';
        }

        const body: any = {
          transactionKey: data.ssid,
          goofyId: GOOFY_ID,
          contentId: data.contentId,
          document: data.document,
          currency: cart.currency,
          email: data.email,
          fullPrice: data.fullPrice,
          name: data.name,
          payment: data.payment.map(p => {
            return {
              brand: p.brand,
              creditCard: p.creditCard,
              paymentMethod: p.paymentMethod,
              saveCard: p.saveCard,
              value: p.value,
              expires: p.expires,
              cvv: p.cvv,
              name: p.name,
              installments: cart.currency === 'USD' ? 1 : p.numberInstallments || 1,
              document: p.document,
              adyenHash: p.adyenHash
            };
          }),
          personType: data.personType,
          phone: data.phone,
          productType: data.productType,
          quantity: data.quantity,
          captcha: data.captchaToken || null,
          paymentMethod,
          parameters
        };

        if (data.shipping) {
          body.shipping = data.shipping;
        }

        if (data.address && data.address.postalCode) {
          body.address = {
            cityId: data.address.cityId || null,
            street: data.address.street || null,
            complement: data.address.complement || null,
            district: data.address.district || null,
            postalCode: data.address.postalCode || null,
            number: data.address.number || null,
            province: data.address.province || null,
            countryId: data.address.country || null
          };
        }

        return apiService
          .post<IPaymentResult>(url, body, null, { maxAttempts: 1 })
          .map(r => {
            this.hasPaymentResult = true;

            if (
              window.location.protocol === 'https:' &&
              cart.config.template.id !== 16 &&
              r.thankyouUrl &&
              r.thankyouUrl.match(/^http:\/\//gi) &&
              !r.redirectTo
            ) {
              console.log('must redirect to http');
              r.redirectTo = `http://${window.location.host}/thankyou/${cart.contentId}/${r.key}`;
            }

            r.personType = data.personType;
            r.brand = data.payment[0].brand;

            this.paymentResult$.next(r);

            return r;
          });
      })
      .finally(() => {
        this.setData({ captchaToken: null });
        this.paymentInProgress = false;
      })
      .logError();
  }

  hasPayment() {
    return this.hasPaymentResult;
  }

  loadPaymentByHash(
    transactionKey: string,
    parameters: any
  ): Observable<IThankYouPage> {
    return apiService.get(`/thankyou/${transactionKey}`, parameters).logError();
  }

  getPaymentResult(): Observable<IPaymentResult> {
    return this.paymentResult$.asObservable();
  }

  getCart(): Observable<ICartModel> {
    return this.data$
      .asObservable()
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  getCurrentCart(): ICartModel {
    return JSON.parse(JSON.stringify(this.data$.value));
  }

  getPayment(index: number) {
    return this.getCart()
      .map(c => c.payment[index])
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  getPaymentInstallments(index: number) {
    return this.getCart()
      .filter(cart => !!cart.payment[index])
      .map(cart => cart.payment[index])
      .map(payment => {
        return {
          installments: payment.installments,
          numberInstallments: payment.numberInstallments
        };
      })
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  isMultipleCreditCards(): Observable<boolean> {
    return this.getCart().map(cart => cart.payment.length > 1);
  }

  getAddress() {
    return this.getCart()
      .map(c => c.address || {})
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  getCoupon() {
    return this.getCart()
      .map(c => c.coupon || {})
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  setData(obj: Partial<ICartModel> = {}): void {
    if (obj.payment) {
      throw new Error('Invalid property, use setPaymentData');
    }

    if (obj.address) {
      throw new Error('Invalid property, use setAddressData');
    }

    this.state = { ...this.state, ...obj };

    this.emit();
  }

  setPaymentData(index: number, obj: Partial<IPaymentModel> = {}) {
    const payment = this.state.payment[index];

    if (obj.creditCard) {
      obj.brand = brandService.getCardBrandByNumber(obj.creditCard);
    }

    this.state.payment[index] = { ...payment, ...obj };
    this.emit();
  }

  handleChangeSaveCard(index: number, saveCard: boolean) {
    const currentPayments = this.state.payment;
    currentPayments.map((payment, i) => {
      this.state.payment[i] = {
        ...payment,
        saveCard: index === i ? saveCard : false
      };
    });

    this.emit();
  }

  addPayment(): Observable<any> {
    if (this.state.payment.length >= MAX_CREDIT_CARDS) {
      return Observable.empty();
    }
    return cartService
      .getCart()
      .take(1)
      .map(cart => cart.price)
      .switchMap(price => {
        const currentIndex = this.state.payment.length - 1;
        const currentPayment = this.state.payment[currentIndex];

        const lastValue = currentPayment.value;
        const newValue = round2(lastValue / 2);

        currentPayment.value = newValue;

        let sumValueAllPayments = newValue;
        this.state.payment.forEach(payment => {
          sumValueAllPayments += payment.value;
        });

        const valueNewPayment = round2(newValue + price - sumValueAllPayments);

        return Observable.combineLatest(
          getInstallments([newValue, valueNewPayment]),
          Observable.of(currentPayment),
          Observable.of(valueNewPayment),
          parameterService.get(enParameters.pf)
        );
      })
      .map(([installments, currentPayment, valueNewPayment, pf]) => {
        const _installments = Object.entries(installments);

        const installmentsNewPayment = _installments.find(
          i => Number(i[0]) === valueNewPayment
        )[1];
        const installmentscurrentPayment = _installments.find(
          i => Number(i[0]) === currentPayment.value
        )[1];

        currentPayment.installments = installmentscurrentPayment.installments;
        currentPayment.numberInstallments = installmentscurrentPayment.maxInstallments;

        if (pf === '1') {
          currentPayment.installments = [{ installment: 0, value: 0 }];
          currentPayment.numberInstallments = 0;
        }

        const newPayment = {
          value: round2(valueNewPayment),
          installments: installmentsNewPayment.installments,
          numberInstallments: installmentsNewPayment.maxInstallments
        };

        const payment = [...this.state.payment, newPayment];
        this.state.payment = payment;

        this.emit();
      });
  }

  removePayment(index: number, numberCreditCards: number = 1) {
    if (this.state.payment.length > 1) {
      const payment = this.state.payment;
      payment.splice(index, numberCreditCards);

      this.state.payment = payment;
      this.emit();

      if (payment.length == 1) {
        cartService
          .getCart()
          .take(1)
          .map(cart => cart.price)
          .subscribe(price => {
            payment[0].value = price;
            this.state.payment = payment;
            this.emit();
          });
      }
    }
  }

  setAddressData(obj: Partial<IUserAddressModel> = {}) {
    const address = this.state.address || {};
    this.state.address = { ...address, ...obj };
    langService
      .getLang()
      .take(1)
      .filter(lang => lang == enLang.PT_BR)
      .switchMap(() => getCitiesByState(this.state.address.province))
      .take(1)
      .subscribe(cities => {
        const city = cities.find(c => c.name == this.state.address.city);
        if (city) {
          this.state.address.city = city.name;
          this.state.address.cityId = city.id;
        }
        this.emit();
      });

    langService
      .getLang()
      .take(1)
      .filter(lang => lang == enLang.EN_US)
      .subscribe(() => {
        this.emit();
      });
  }

  setCoupon(obj: Partial<ICouponModel> = {}) {
    const coupon = this.state.coupon || {};
    this.state.coupon = { ...coupon, ...obj };
    this.emit();
  }

  showPaymentMultipleCardsButton(): Observable<boolean> {
    return cartService
      .getCart()
      .take(1)
      .combineLatest(this.getCart(), langService.getLang())
      .switchMap(([cart, payment, lang]) => {
        if (lang !== enLang.PT_BR) {
          this.removePayment(1, payment.payment.length - 1);

          return Observable.of(false);
        }

        const isMultipleCreditCards = cart.config.multipleCreditCards;
        const hasOnePaymentForm = payment.payment.length <= 1;
        const boletoPaypalSelected =
          payment.payment[0].paymentMethod == enPaymentMethod.BOLETO ||
          payment.payment[0].paymentMethod == enPaymentMethod.PAYPAL;

        return Observable.of(
          isMultipleCreditCards && hasOnePaymentForm && !boletoPaypalSelected
        );
      });
  }

  public loadParameters() {
    parameterService.getAll().subscribe(parameters => {
      Object.keys(parameters).map(p => {
        if (p === enParameters.doc) {
          this.setData({ document: parameters[p] });
        } else if (p === enParameters.name || p === enParameters.nome) {
          this.setData({ name: parameters[p] });
        } else if (p === enParameters.email) {
          this.setData({ email: parameters[p] });
        } else if (p === enParameters.cel || p === enParameters.phone) {
          this.setData({ phone: parameters[p] });
        } else if (p === enParameters.cep || p === enParameters.zip) {
          let postalCode = parameters[p];

          if (!(postalCode.indexOf('-') > -1) && postalCode.length == 8) {
            postalCode =
              postalCode.substr(0, 5) + '-' + postalCode.substr(4 + 1);
          }

          this.setAddressData({ postalCode });
        } else if (p === enParameters.num) {
          this.setAddressData({ number: Number(parameters[p]) });
        } else if (p === enParameters.comp) {
          this.setAddressData({ complement: parameters[p] });
        } else if (p === enParameters.p) {
          this.setDefaultInstallment(Number(parameters[p]));
        } else if (p === enParameters.pf) {
          this.setPaymentData(0, { numberInstallments: 0 });
        }
      });
    });

    parameterService
      .get(enParameters.cupom)
      .take(1)
      .filter(coupon => !!coupon)
      .switchMap(coupon => cartService.setCoupon(coupon))
      .subscribe(
        () => { },
        err => {
          showAlert(
            'Há algo errado com o seu cupom, por favor entre em contato com o vendedor',
            'Ops!'
          ).subscribe();
        }
      );
  }

  public setDefaultInstallment(installmentSelected: number) {
    const maxNumberInstallment = Math.max.apply(
      Math,
      this.state.payment[0].installments.map(i => i.installment)
    );
    const numberInstallments =
      maxNumberInstallment >= installmentSelected
        ? installmentSelected
        : maxNumberInstallment;

    this.setPaymentData(0, { numberInstallments });
  }

  public getNormalizedFrequencyType(type: enFrequencyType, frequency: number) {
    if (type == enFrequencyType.DIARIO) {
      return frequency > 1 ? 'Dias' : 'Dia';
    }

    if (type == enFrequencyType.MENSAL) {
      return frequency > 1 ? 'Meses' : 'Mês';
    }

    if (type == enFrequencyType.ANUAL) {
      return frequency > 1 ? 'Anos' : 'Ano';
    }

    return '';
  }

  public getNormalizedFrequencyTypeEnUs(
    type: enFrequencyType,
    frequency: number
  ) {
    if (type == enFrequencyType.DIARIO) {
      return frequency > 1 ? 'days' : 'day';
    }

    if (type == enFrequencyType.MENSAL) {
      return frequency > 1 ? 'months' : 'month';
    }

    if (type == enFrequencyType.ANUAL) {
      return frequency > 1 ? 'years' : 'year';
    }

    return '';
  }

  public getNormalizedPaymentType(
    type: enPaymentMethod
  ): enNormalizedPaymentType {
    if (type === enPaymentMethod.PAYPAL) {
      hotJarService.log('paypal', true);
      return enNormalizedPaymentType.PAYPAL;
    }

    if (type === enPaymentMethod.BOLETO) {
      hotJarService.log('bankslip', true);
      return enNormalizedPaymentType.BANKSLIP;
    }

    if (type === enPaymentMethod.FREE) {
      hotJarService.log('free', true);
      return enNormalizedPaymentType.FREE;
    }

    if (type === enPaymentMethod.ONE_CLICK_BUY) {
      hotJarService.log('oneClick', true);
      return enNormalizedPaymentType.ONE_CLICK;
    }

    hotJarService.log('creditCard', true);
    return enNormalizedPaymentType.CREDIT_CARD;
  }

  private normalizePaymentData(
    adyen: any,
    data: ICartModel,
    lang: enLang
  ): ICartModel {
    const cse = adyen.createEncryption(ADYEN_KEY, {});

    data.payment.forEach(p => {
      p.paymentMethod = <any>this.getNormalizedPaymentType(p.paymentMethod);

      if (p.creditCard) {
        const [month, year] = (p.expires || '').split('/');

        const cardData = {
          number: p.creditCard,
          cvc: p.cvv,
          holderName: p.name,
          expiryMonth: month,
          expiryYear: `20${year}`,
          generationtime: new Date().toISOString()
        };

        p.adyenHash = cse.encrypt(cardData);
        p.secret = `${p.creditCard.substring(
          0,
          2
        )}** **** **** **${p.creditCard.substring(p.creditCard.length - 2)}`;
      }

      if (p.paymentMethod !== enPaymentMethod.FREE && lang !== enLang.EN_US) {
        data.personType = data.document.length == 14 ? 'J' : 'F';
      }

      if (lang === enLang.EN_US) {
        data.personType = 'E';
      }
    });

    return data;
  }

  private emit() {
    const data = JSON.parse(JSON.stringify(this.state));
    this.data$.next(data);
  }
}

// tslint:disable-next-line:variable-name
export const PaymentServiceFactory = () => new PaymentService();

export const paymentService = PaymentServiceFactory();



// WEBPACK FOOTER //
// ./src/services/PaymentService.ts